/**
 * Author-facing values for whether a CTA opens in the current tab or a new one. Named for the
 * outcome rather than the `target` keyword. Popup, video, and download are actions, so they stay
 * separate from this same-tab/new-tab choice.
 */
export const CTA_BEHAVIORS = ['sameTab', 'newTab'] as const;
export type CtaBehavior = (typeof CTA_BEHAVIORS)[number];

/**
 * Screen-reader-only warning attached via `aria-describedby` to every new-tab CTA, so the tab
 * change is announced before activation regardless of any visible icon (WCAG technique G201).
 */
export const CTA_NEW_TAB_DESCRIPTION = 'Opens in a new tab';

/**
 * Space-joined `aria-describedby` value, dropping unset ids so a CTA with neither subtext nor a
 * new-tab warning gets no attribute at all.
 */
export const joinDescribedBy = (...ids: Array<string | undefined>): string | undefined => {
  const joined = ids.filter(Boolean).join(' ');
  return joined || undefined;
};

/**
 * Anchor attributes a CTA behavior resolves to. Every block that renders an authored CTA spreads
 * this onto its anchor so the `rel` hardening travels with `target="_blank"` and cannot be
 * forgotten at a call site.
 *
 * @param behavior - Authored behavior; `undefined` and `'sameTab'` both mean the current tab.
 * @returns `target` and `rel` for a new tab, or an empty object.
 */
export const ctaTargetAttributes = (
  behavior?: CtaBehavior
): { target?: '_blank'; rel?: 'noopener noreferrer' } =>
  behavior === 'newTab' ? { target: '_blank', rel: 'noopener noreferrer' } : {};
